'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';

/**
 * Root-level error boundary. Reports uncaught client errors to:
 *   1. Sentry (if NEXT_PUBLIC_SENTRY_DSN is configured)
 *   2. /api/log/error (always — internal mirror to Supabase error_log)
 * Then renders an institutional fallback UI.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // 1. Sentry — primary observability surface
    try { Sentry.captureException(error); } catch { /* SDK not init'd, skip */ }

    // 2. Internal mirror so we still have a record if Sentry is down /
    //    if the user blocked third-party scripts via consent
    fetch('/api/log/error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: error?.message || 'unknown',
        digest: error?.digest,
        stack: error?.stack?.slice(0, 4000),
        url: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      }),
    }).catch(() => { /* swallow */ });

    // eslint-disable-next-line no-console
    console.error('[mcc] uncaught:', error);
  }, [error]);

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: 'var(--mcc-surface)', minHeight: '100vh' }}>
        <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ maxWidth: 520, textAlign: 'center', background: 'white', border: '1px solid var(--mcc-border)', borderRadius: 12, padding: '40px 32px' }}>
            <div style={{ fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--mcc-warn-fg)', marginBottom: 12 }}>
              Marine Conservation Credits
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--mcc-deep)', margin: '0 0 12px' }}>
              Something went wrong
            </h1>
            <p style={{ fontSize: 14, color: 'var(--mcc-muted)', lineHeight: 1.6, margin: '0 0 24px' }}>
              The page encountered an unexpected error and could not be rendered. Our team has been notified. Please retry, or return to the home page.
            </p>
            {error?.digest && (
              <div style={{ fontSize: 12, color: 'var(--mcc-muted)', fontFamily: 'monospace', marginBottom: 24 }}>
                Reference: {error.digest}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => reset()}
                style={{ background: 'var(--mcc-deep)', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Try again
              </button>
              <a
                href="/"
                style={{ background: 'white', color: 'var(--mcc-deep)', border: '1px solid var(--mcc-border)', padding: '10px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
              >
                Return home
              </a>
            </div>
          </div>
        </main>
      </body>
    </html>
  );
}
